// Content extracted from "Blueyardiq Manual.pdf" (UserManual v1.0, March 2026)
// Screenshots extracted from the same PDF into /manual-images/
const IMG = '/manual-images';

export const manualMeta = {
    title: 'BlueyardIQ User Manual',
    subtitle: 'Step-by-Step Operating Guide',
    version: '1.0',
    organization: 'iShip Solutions Pte. Ltd',
    lastUpdated: 'March 2026',
    classification: 'Internal Use Only',
    intro:
        'BlueyardIQ is a comprehensive project management and quality assurance platform designed for new-building ship activities. This manual guides users through every module from account setup to final delivery documentation.',
    intendedFor: ['End Users', 'QA Inspectors', 'Project Coordinators', 'Supervisors', 'Administrators'],
};

// Each section: { id, number, title, blocks: [] }
// Block types:
//   { type: 'p', text }
//   { type: 'steps', title?, items: [] }
//   { type: 'note', variant: 'note'|'tip'|'important', text }
//   { type: 'image', src, caption }
//   { type: 'table', head: [], rows: [[]] }
export const manualSections = [
    {
        id: 'getting-started',
        number: '1',
        title: 'Getting Started',
        blocks: [
            {
                type: 'steps',
                title: '1.1 Prerequisites',
                items: [
                    'A valid BlueyardIQ user account.',
                    'Correct role/module access assigned by your administrator.',
                    'Browser access to the specific BlueyardIQ URL.',
                    'A stable internet connection.',
                ],
            },
            {
                type: 'p',
                text:
                    'Access to BlueyardIQ is strictly role-based. Your designation (e.g., QA Inspector, Project Manager) determines which modules appear in your top menu. If a module is missing, contact your administrator to update your Permission IDs.',
            },
            {
                type: 'steps',
                title: '1.3 How to Log In',
                items: [
                    'Open the BlueyardIQ login page in your web browser.',
                    'Enter your registered Username.',
                    'Enter your Password.',
                    'Click Login to access the Dashboard.',
                ],
            },
            { type: 'image', src: `${IMG}/img-048.png`, caption: 'BlueyardIQ login screen' },
            {
                type: 'note',
                variant: 'note',
                text:
                    'Troubleshooting Login: If login fails, check your internet connection, verify credentials, use the "Forgot Password" link, or contact your system administrator if your account is locked.',
            },
            {
                type: 'steps',
                title: '1.4 Forgot Password',
                items: [
                    'Click "Forgot Password" on the login screen.',
                    'Enter your registered username/email.',
                    'Check your system email for reset instructions.',
                    'Follow the link to set a new secure password.',
                ],
            },
            {
                type: 'steps',
                title: '1.5 Lock Screen & Logout',
                items: [
                    'Click your profile icon in the top-right corner.',
                    'Select Lock Screen to pause your session.',
                    'To sign out completely, select Logout from the dropdown menu.',
                ],
            },
        ],
    },
    {
        id: 'navigating',
        number: '2',
        title: 'Navigating the Interface',
        blocks: [
            {
                type: 'steps',
                title: '2.1 Header Bar',
                items: [
                    'Company Logo: Returns you to the Dashboard.',
                    'Module Menu: Navigation links to functional areas.',
                    'Notification Bell: Alerts for tasks, chats, and approvals.',
                    'Profile Menu: Settings, Mailbox config, and Logout.',
                ],
            },
            { type: 'image', src: `${IMG}/img-053.png`, caption: 'Header bar and module navigation' },
            {
                type: 'p',
                text:
                    'The top menu lists available modules such as Schedule, Document, Plan Approval, Inspection, NCR, FAT, Final Drawing, Forms, Master, and Administration.',
            },
            { type: 'note', variant: 'note', text: 'You will only see modules assigned to your specific user role.' },
            {
                type: 'steps',
                title: '2.3 Notification System',
                items: [
                    'Locate the bell icon in the top-right header.',
                    'A red badge indicates the count of unread items.',
                    'Click the bell to see a dropdown of recent alerts (Chat, Tags, Assignments).',
                    'Click "View All" to go to the full notification center.',
                ],
            },
        ],
    },
    {
        id: 'dashboard',
        number: '3',
        title: 'Dashboard',
        blocks: [
            { type: 'p', text: 'Your Daily Control Center provides a real-time snapshot of project health.' },
            {
                type: 'steps',
                title: '3.1 Dashboard Overview',
                items: [
                    'Summary Cards: Counts for Plans, FAT, NCRs, and Inspections.',
                    'My Priorities: A personalized feed of urgent tasks assigned to you.',
                    'Statistics: Monthly activity graphs and project counts.',
                    'Latest Projects: Quick access to active project cards.',
                ],
            },
            { type: 'image', src: `${IMG}/img-054.png`, caption: 'Dashboard control center' },
            {
                type: 'steps',
                title: '3.2 Daily Start Routine',
                items: [
                    'Log in and land on the Dashboard.',
                    'Check the Notification Bell for immediate alerts.',
                    'Review the "My Priorities" list for urgent items.',
                    'Glance at Summary Cards (NCR/FAT) to gauge workload.',
                    'Check the "Latest Projects" list for new assignments.',
                    'Approvers: Check the "Leave Requests" list for pending approvals.',
                ],
            },
            {
                type: 'steps',
                title: '3.3 Working with Latest Projects',
                items: [
                    'Scroll to the "Latest Projects" section.',
                    'Click the View icon (+) to see project details.',
                    'Click the Edit icon (pencil) to update project metadata.',
                    'Click the Assign icon to map vessels/hulls to the project.',
                    'Use the Delete icon to delete the project.',
                ],
            },
        ],
    },
    {
        id: 'schedule',
        number: '4',
        title: 'Schedule Module',
        blocks: [
            {
                type: 'steps',
                title: '4.1 Creating a Schedule Event',
                items: [
                    'Navigate to Schedule in the top menu.',
                    'Click the Add Event button.',
                    'Select the relevant Project and Vessel.',
                    'Choose a Category and Event Type.',
                    'Enter Start/End dates and description notes.',
                    'Click Save to add to the calendar.',
                ],
            },
            { type: 'image', src: `${IMG}/img-059.png`, caption: 'Creating a schedule event' },
            {
                type: 'steps',
                title: '4.2 Searching and Reviewing Events',
                items: [
                    'Open the Schedule List view.',
                    'Use filters for Project, Date Range, or Category.',
                    'Review the filtered list of milestones.',
                    'Click any event row to view full details.',
                ],
            },
            {
                type: 'steps',
                title: '4.3 Updating a Schedule Event',
                items: [
                    'Locate the event in the list or calendar.',
                    'Click the Edit action.',
                    'Modify dates, status, or notes.',
                    'Click Update to save changes.',
                    'Verify the change is reflected in the schedule.',
                ],
            },
            { type: 'image', src: `${IMG}/img-064.png`, caption: 'Updating a schedule event' },
            {
                type: 'steps',
                title: '4.4 Managing Schedule Categories',
                items: [
                    'Go to Master > Schedule Category.',
                    'Click Create to define a new milestone category.',
                    'Save and ensure it is set to "Active".',
                ],
            },
            {
                type: 'note',
                variant: 'note',
                text:
                    'Schedule categories must be set up in the Master module before they can be used in the Schedule module.',
            },
        ],
    },
    {
        id: 'document',
        number: '5',
        title: 'Document Module',
        blocks: [
            {
                type: 'steps',
                title: '5.1 Uploading a New Document',
                items: [
                    'Navigate to the Document module.',
                    'Click Add Document.',
                    'Select the Project context.',
                    'Select the specific Ship/Hull.',
                    'Choose the Document Type (e.g., Drawing, Manual).',
                    'Enter the Title and Reference Number.',
                    'Drag and drop file(s) to upload.',
                    'Click Save.',
                ],
            },
            { type: 'image', src: `${IMG}/img-069.png`, caption: 'Uploading a new document' },
            {
                type: 'p',
                text:
                    'Documents can be viewed in two main ways: Project-wise (for counts and summaries) or Ship/Hull-wise (for specific file lists).',
            },
            {
                type: 'steps',
                title: '5.2 Viewing Documents',
                items: [
                    'Select Document List (Hull) from the menu.',
                    'Filter by Ship Name to see the repository.',
                    'Click the document title to open the detail view.',
                    'Click the download icon to save the file locally.',
                ],
            },
            { type: 'image', src: `${IMG}/img-072.png`, caption: 'Viewing documents by hull' },
            {
                type: 'steps',
                title: '5.3 Adding Comments',
                items: [
                    'Open the Document Detail view.',
                    'Type your message in the "Add Comment" box.',
                    'Click Submit. This creates an audit trail for the file.',
                ],
            },
        ],
    },
    {
        id: 'plan-approval',
        number: '6',
        title: 'Plan Approval Module',
        blocks: [
            {
                type: 'steps',
                title: '6.1 Raising a New Plan Approval Item',
                items: [
                    'Open Plan Approval and select a project.',
                    'Click Create.',
                    'Enter Plan Title, Reference, and Type.',
                    'Upload the initial drawing files.',
                    'Set the initial status (e.g., Draft).',
                    'Click Save to generate the record.',
                ],
            },
            { type: 'image', src: `${IMG}/img-074.png`, caption: 'Raising a new plan approval item' },
            {
                type: 'steps',
                title: '6.2 Assigning Stakeholders and Reviewing',
                items: [
                    'Open the created plan record.',
                    'Use the Assign action to add reviewers.',
                    'Post a comment to initiate the review cycle.',
                    'Stakeholders receive notifications to review.',
                    'Reviewers add replies and mark comments as "Favorite" if critical.',
                    'Manage ownership if responsibilities change.',
                ],
            },
            {
                type: 'steps',
                title: '6.3 Handling Revisions',
                items: [
                    'In the Plan Detail view, click Upload Revision.',
                    'Attach the updated file version.',
                    'Add a note describing changes.',
                    'The system tracks version history automatically.',
                ],
            },
            { type: 'image', src: `${IMG}/img-076.png`, caption: 'Handling plan revisions' },
            {
                type: 'table',
                title: '6.4 Plan Status Reference',
                head: ['Status Name', 'Definition'],
                rows: [
                    ['Draft', 'Initial stage; not yet submitted for formal review.'],
                    ['Approved', 'Plan is accepted as is. No changes needed.'],
                    ['Approved Subjected to Comments', 'Accepted provided that noted changes are made.'],
                    ['Not Accepted', 'Rejected; significant changes required.'],
                    ['Reference', 'Document is for information only.'],
                ],
            },
            {
                type: 'steps',
                title: '6.5 Finalizing and Distributing',
                items: [
                    'Once review is complete, update Plan Status to Final.',
                    'Click Send Report Mail to notify stakeholders.',
                    'Download the final ZIP archive for project records.',
                ],
            },
        ],
    },
    {
        id: 'fat',
        number: '7',
        title: 'FAT (Factory Acceptance Test)',
        blocks: [
            {
                type: 'steps',
                title: '7.1 Creating a FAT Record',
                items: [
                    'Navigate to FAT and select a project.',
                    'Click Create FAT.',
                    'Select the Ship/Hull context.',
                    'Enter the Subject and testing requirements.',
                    'Click Save.',
                ],
            },
            { type: 'image', src: `${IMG}/img-082.png`, caption: 'Creating a FAT record' },
            {
                type: 'table',
                title: '7.2 Assigning Roles',
                head: ['Assigned User', 'Submitted User', 'Attended Users'],
                rows: [['Responsible for the record.', 'Submits test results.', 'Witnessed the test.']],
            },
            {
                type: 'steps',
                items: [
                    'Open the FAT record detail.',
                    'Select users for each role category.',
                    'Save assignments to trigger notifications.',
                ],
            },
            {
                type: 'steps',
                title: '7.3 Recording Evidence and Updating Status',
                items: [
                    'Execute the test. Open FAT Detail view.',
                    'Use comments to log checks performed.',
                    'Use the Attachments section to upload photos/docs.',
                    'Update status from "Scheduled" to "Accepted".',
                    'Save the record.',
                ],
            },
            { type: 'image', src: `${IMG}/img-090.png`, caption: 'Recording FAT evidence' },
            {
                type: 'table',
                title: '7.4 FAT Status Reference',
                head: ['Status'],
                rows: [['Unscheduled'], ['Scheduled'], ['Not Applicable'], ['Accepted with Comments'], ['Accepted']],
            },
            {
                type: 'steps',
                title: '7.5 Reports',
                items: [
                    'Click Send FAT Report on the detail screen.',
                    'Confirm recipients.',
                    'Download the PDF report and artifacts for the QA folder.',
                ],
            },
        ],
    },
    {
        id: 'ncr',
        number: '8',
        title: 'NCR (Non-Conformance Reports)',
        blocks: [
            {
                type: 'steps',
                title: '8.1 Creating an NCR',
                items: [
                    'Open NCR module.',
                    'Click Create NCR.',
                    'Select Project and Ship context.',
                    'Enter full description of the non-conformance.',
                    'Save to generate the NCR ID.',
                ],
            },
            { type: 'image', src: `${IMG}/img-094.png`, caption: 'Creating an NCR' },
            {
                type: 'steps',
                title: '8.2 Adding Comments and Collaborating',
                items: [
                    'Open NCR Detail view.',
                    'Add a comment requesting Root Cause Analysis (RCA).',
                    'Assign an internal owner.',
                    'Use threaded replies to discuss corrective actions.',
                    'Upload photos/evidence directly in comments.',
                ],
            },
            {
                type: 'steps',
                title: '8.3 Tracking to Closure',
                items: [
                    'Update NCR status (Draft → Open).',
                    'Verify corrective action completion physically.',
                    'Mark individual comments/actions as "Accepted".',
                    'Check Activity Log for audit trail.',
                    'Change Status to Close.',
                ],
            },
            { type: 'image', src: `${IMG}/img-100.png`, caption: 'Tracking an NCR to closure' },
            {
                type: 'table',
                title: '8.4 NCR Status Reference',
                head: ['NCR Status', 'Comment Status'],
                rows: [['Draft', 'Draft'], ['Open', 'Opened'], ['Close', 'Accepted / Archived']],
            },
            {
                type: 'note',
                variant: 'important',
                text:
                    'Do not close an NCR until root cause and corrective actions are fully documented and verified.',
            },
        ],
    },
    {
        id: 'inspection',
        number: '9',
        title: 'Inspection Module',
        blocks: [
            {
                type: 'steps',
                title: '9.1 Scheduling an Inspection',
                items: [
                    'Go to Inspection > Create.',
                    'Select Project and Vessel.',
                    'Enter planned date and inspection details.',
                    'Assign an Inspector.',
                    'Save the record.',
                    'Inspector receives a notification.',
                ],
            },
            { type: 'image', src: `${IMG}/img-101.png`, caption: 'Scheduling an inspection' },
            {
                type: 'steps',
                title: '9.2 Recording Attendance',
                items: [
                    'Open Inspection Detail.',
                    'Click Add Attendance.',
                    'Enter names of participants.',
                    'Upload sign-in sheet if applicable.',
                    'Save attendance log.',
                ],
            },
            {
                type: 'steps',
                title: '9.3 Adding Photo Evidence',
                items: [
                    'During inspection, access the record.',
                    'Click Attachments or Evidence.',
                    'Upload photos of findings.',
                    'Add specific remarks/observations.',
                    'Update Inspection Status (e.g., Passed/Failed).',
                ],
            },
            { type: 'image', src: `${IMG}/img-104.png`, caption: 'Adding photo evidence to an inspection' },
            {
                type: 'steps',
                title: '9.4 Sending Inspection Report',
                items: [
                    'Ensure all data is entered.',
                    'Click Send Report Mail.',
                    'Select recipients.',
                    'Confirm send.',
                    'Check Activity Log.',
                ],
            },
            {
                type: 'note',
                variant: 'tip',
                text:
                    'Quality Check: ✓ Attendance captured ✓ Observations specific ✓ Photo evidence attached ✓ Recipients confirmed',
            },
        ],
    },
    {
        id: 'final-drawing',
        number: '10',
        title: 'Final Drawing & Document Management',
        blocks: [
            {
                type: 'steps',
                title: '10.1 Browsing Documents',
                items: [
                    'Go to Final Drawing and click a project to open its document browser.',
                    'The left rail lists folders; click one to see its contents on the right.',
                    'Use the breadcrumb above the table to move back up the folder path.',
                    'Type in the search box to find a document anywhere in the project — results show each file\'s folder path.',
                ],
            },
            { type: 'image', src: `${IMG}/img-106.png`, caption: 'The project document browser' },
            {
                type: 'steps',
                title: '10.2 Creating and Organising Folders',
                items: [
                    'Select the folder you want the new folder to sit inside (or Home for the top level).',
                    'Click New folder, enter a name and Save.',
                    'Folders can be nested as deeply as you need.',
                    'Use the ⋮ menu on any row to Rename, Move or Delete a folder.',
                ],
            },
            {
                type: 'note',
                variant: 'info',
                text:
                    'Existing drawings were filed automatically into folders named after their category, so nothing was lost when the browser replaced the old flat list.',
            },
            {
                type: 'steps',
                title: '10.3 Uploading Files and Tagging a Vessel',
                items: [
                    'Open the destination folder.',
                    'Click Upload and choose one or more files.',
                    'Pick a Vessel to tag the files to that hull, or leave it as Project-wide.',
                    'Click Save.',
                ],
            },
            {
                type: 'steps',
                title: '10.4 Vessel-wise View',
                items: [
                    'Switch the toggle at the top left from Folders to Vessels.',
                    'Pick a vessel from the left rail to see only its documents.',
                    'Choose Project-wide to see files that are not tagged to any hull.',
                    'Choose All vessels to remove the filter again.',
                ],
            },
            {
                type: 'steps',
                title: '10.5 Sharing a Link',
                items: [
                    'Open the ⋮ menu on a file or folder and choose Share.',
                    'Tick Allow download if the recipient may download, not just view.',
                    'Set an expiry date if the link should stop working on its own.',
                    'Click Create link, then Copy and send it to the recipient.',
                ],
            },
            {
                type: 'note',
                variant: 'warning',
                text:
                    'Anyone holding a share link can open it without logging in. Share only what the recipient needs, prefer view-only, and set an expiry date for external parties.',
            },
            {
                type: 'steps',
                title: '10.6 Revoking a Link and Reading the Access Log',
                items: [
                    'Click Shared links in the top bar to list every link for the project.',
                    'The Status column shows Active, Expired or Revoked.',
                    'Click the number in the Opens column to see who opened, previewed or downloaded the files, with date and IP address.',
                    'Click Revoke to kill a link immediately.',
                ],
            },
        ],
    },
    {
        id: 'guaranty-claims',
        number: '11',
        title: 'Guaranty Claims Module',
        blocks: [
            {
                type: 'steps',
                title: '11.1 Creating a Guaranty Claim',
                items: [
                    'Navigate to Guaranty Claim > Create.',
                    'Select Project and Hull.',
                    'Enter claim details and description.',
                    'Attach supporting evidence.',
                    'Save the record.',
                ],
            },
            { type: 'image', src: `${IMG}/img-108.png`, caption: 'Creating a guaranty claim' },
            {
                type: 'p',
                text: 'You can view claims by Hull (specific vessel list) or by Project (counts and overview).',
            },
            {
                type: 'steps',
                title: '11.2 Viewing Claims',
                items: [
                    'Select Guaranty Claim (Hull).',
                    'Filter by specific vessel.',
                    'Review list of open claims.',
                    'Open detail view for full history.',
                ],
            },
            {
                type: 'steps',
                title: '11.3 Tracking Status',
                items: [
                    'Open Project Guaranty Claim Count.',
                    'Check status columns.',
                    'Update status as claims are processed.',
                    'Verify updates in the list view.',
                ],
            },
            { type: 'image', src: `${IMG}/img-114.png`, caption: 'Tracking guaranty claim status' },
        ],
    },
    {
        id: 'production-forms',
        number: '12',
        title: 'Forms: Production Forms',
        blocks: [
            { type: 'p', text: 'Configure → Submit → Pending Review → Verified' },
            {
                type: 'steps',
                title: '12.1 Pre-Launching Checklist',
                items: [
                    'Open Pre-Launching Checklist form.',
                    'Configure template if needed.',
                    'Select Project/Hull.',
                    'Fill mandatory check items.',
                    'Attach evidence.',
                    'Submit.',
                    'Verify in Submitted list.',
                ],
            },
            { type: 'image', src: `${IMG}/img-115.png`, caption: 'Pre-Launching checklist' },
            {
                type: 'steps',
                title: '12.2 Pre-Delivery Checklist',
                items: [
                    'Open Pre-Delivery Checklist.',
                    'Fill line items.',
                    'Attach photos.',
                    'Submit form.',
                    'Supervisor reviews in Pending queue.',
                    'Form marked verified.',
                ],
            },
            {
                type: 'steps',
                title: '12.3 Sea Trials Checklist',
                items: [
                    'Open Sea Trials form.',
                    'Record test parameters.',
                    'Upload trial data/evidence.',
                    'Submit for review.',
                    'Check submission status.',
                    'Archive report.',
                ],
            },
            { type: 'image', src: `${IMG}/img-118.png`, caption: 'Sea Trials checklist' },
            {
                type: 'steps',
                title: '12.4 Block Inspection Status Masterlist',
                items: [
                    'Open Block Inspection form.',
                    'Identify Block ID.',
                    'Inspect structural items.',
                    'Mark status for each item.',
                    'Upload photos of defects.',
                    'Submit.',
                    'Supervisor approves.',
                ],
            },
            {
                type: 'steps',
                title: '12.5 Daily Inspection Report',
                items: [
                    'Open Daily Inspection form.',
                    'Enter date and location.',
                    'Log daily observations.',
                    'Attach site photos.',
                    'Submit report.',
                    'Reviewers process from Pending queue.',
                ],
            },
            { type: 'image', src: `${IMG}/img-120.png`, caption: 'Daily Inspection report' },
        ],
    },
    {
        id: 'site-forms',
        number: '13',
        title: 'Forms: Site Management Forms',
        blocks: [
            {
                type: 'steps',
                title: '13.1 Site Query Report',
                items: [
                    'Open Site Query form.',
                    'Select reporting context (Project).',
                    'Enter query details.',
                    'Upload documents.',
                    'Submit.',
                    'Verify in Submitted list.',
                ],
            },
            {
                type: 'steps',
                title: '13.2 Potential Variation Order (PVO)',
                items: [
                    'Open PVO Submission form.',
                    'Describe variation.',
                    'Estimate cost/time impact.',
                    'Attach justification.',
                    'Submit for approval.',
                    'Track status in register.',
                ],
            },
            { type: 'image', src: `${IMG}/img-126.png`, caption: 'Site management forms' },
            {
                type: 'steps',
                title: '13.3 Customer Property Inventory',
                items: [
                    'Open Inventory form.',
                    'Select Project/Ship.',
                    'List property items and quantity.',
                    'Upload condition photos.',
                    'Submit.',
                    'Verify receipt.',
                ],
            },
            {
                type: 'steps',
                title: '13.4 Site Audit Index',
                items: [
                    'Open Site Audit form.',
                    'Enter audit scope and date.',
                    'Log findings.',
                    'Upload evidence.',
                    'Submit report.',
                    'Reviewers process from Pending queue.',
                ],
            },
            {
                type: 'steps',
                title: '13.5 Bi-Weekly and Monthly Reports',
                items: [
                    'Open Monthly Report form.',
                    'Select reporting period.',
                    'Fill section-wise progress data.',
                    'Attach charts/photos.',
                    'Submit to management.',
                ],
            },
            { type: 'image', src: `${IMG}/img-128.png`, caption: 'Bi-weekly and monthly reports' },
        ],
    },
    {
        id: 'master',
        number: '14',
        title: 'Master Module',
        blocks: [
            {
                type: 'note',
                variant: 'tip',
                text: 'Set up Master data (Yards, Owners, Locations) before onboarding new projects.',
            },
            {
                type: 'steps',
                title: '14.1 Projects',
                items: [
                    'Go to Project > Add.',
                    'Enter Project Manager, Dates, Metadata.',
                    'Save.',
                    'Go to Project List > Assign Vessels.',
                    'Map vessels to project.',
                ],
            },
            { type: 'image', src: `${IMG}/img-123.png`, caption: 'Master module — Projects' },
            {
                type: 'steps',
                title: '14.2 Vessel Owners',
                items: [
                    'Go to Ship Owner > Create.',
                    'Enter Owner details (name, contact, address).',
                    'Save and verify in the Ship Owner List.',
                ],
            },
            {
                type: 'steps',
                title: '14.3 Shipyards',
                items: [
                    'Go to Ship Yard > Create.',
                    'Enter Yard details and location.',
                    'Save and verify in the Ship Yard List.',
                ],
            },
            {
                type: 'steps',
                title: '14.4 Ship Types',
                items: [
                    'Go to Ship Type > Create.',
                    'Define the vessel classification.',
                    'Save and verify in the Ship Type List.',
                ],
            },
            {
                type: 'steps',
                title: '14.5 Site Locations',
                items: [
                    'Go to Site Location > Create.',
                    'Define Country, State, City, and District.',
                    'Save and verify in the Site Location List.',
                ],
            },
            {
                type: 'steps',
                title: '14.6 Categories and Schedule Categories',
                items: [
                    'Go to Master > Schedule Category.',
                    'Click Create and enter the category name.',
                    'Define Category Events under each category.',
                    'Save and set status to Active.',
                ],
            },
        ],
    },
    {
        id: 'mailbox-chat',
        number: '15',
        title: 'Mailbox & Chat',
        blocks: [
            {
                type: 'steps',
                title: '15.1 Configuring Your Mailbox',
                items: [
                    'Go to User Profile > Mail Settings.',
                    'Click Add mailbox and choose the email provider.',
                    'Authenticate with Microsoft to start the integration flow.',
                    'Complete the callback process.',
                    'Verify the new account appears in the mailbox list on the left.',
                    'Select a mailbox, search for users in People with access, and add or remove them. Click Save access to apply changes. Owners and admins always have access.',
                    'Owners and admins can use Remove mailbox to disconnect an account and clear its assigned access.',
                    'Use Open mailbox to return to the Mailbox module.',
                ],
            },
            { type: 'image', src: `${IMG}/img-127.png`, caption: 'Configuring your mailbox' },
            {
                type: 'steps',
                title: '15.2 Reading and Managing Emails',
                items: [
                    'Open the Mailbox module.',
                    'Select a mailbox you own, one shared with you, or any mailbox if you are an admin; then choose a folder (e.g., INBOX).',
                    'Use the search box to find specific messages.',
                    'Click any email to open its full content.',
                    'Download attachments directly from the email view.',
                ],
            },
            {
                type: 'steps',
                title: '15.3 Email Tagging for Collaboration',
                items: [
                    'Open a target email in the Mailbox.',
                    'Click the Tag action to assign ownership to a team member.',
                    'The tagged user receives a real-time notification.',
                    'Add an internal comment to indicate next actions or context.',
                    'Review the Activity Tab to see the full collaboration history.',
                ],
            },
            { type: 'image', src: `${IMG}/img-133.png`, caption: 'Email tagging for collaboration' },
            {
                type: 'steps',
                title: '15.4 Composing Emails',
                items: [
                    'Click Compose in the Mailbox view.',
                    'Enter recipients, subject, and body text.',
                    'Attach files if needed.',
                    'Click Send to dispatch the email.',
                ],
            },
            {
                type: 'steps',
                title: '15.5 Internal Chat',
                items: [
                    'Open the Chat Panel from the header.',
                    'Select a user from the contact list.',
                    'Type your message and hit Send.',
                    'Attach files within the chat thread if needed.',
                    'Review sent/received history in the thread panel.',
                    'Use the notification bell to stay alerted to incoming messages.',
                ],
            },
            {
                type: 'note',
                variant: 'tip',
                text:
                    'Ensure every actionable email has an owner tag. Use internal comments to document the required next steps for project handovers.',
            },
        ],
    },
    {
        id: 'administration',
        number: '16',
        title: 'Administration',
        blocks: [
            {
                type: 'p',
                text:
                    'Administration tools allow authorized users to manage staff, permissions, and global system settings.',
            },
            {
                type: 'steps',
                title: '16.1 Attendance Management — User Submission',
                items: ['Navigate to Attendance > Submit.', 'Fill in your daily attendance record and save.'],
            },
            {
                type: 'steps',
                title: '16.1 Attendance Management — Supervisor Review',
                items: [
                    'Go to Attendance > Project View.',
                    'Select the project to review staff attendance.',
                    'Access Monthly Reports for project-wise or user-wise summaries.',
                ],
            },
            { type: 'image', src: `${IMG}/img-133.png`, caption: 'Attendance management' },
            {
                type: 'steps',
                title: '16.2 Leave Management — Employee Flow',
                items: [
                    'Go to Leave > Apply to submit a leave request.',
                    'Track status via My Requests.',
                    'Edit request if still pending using Leave Edit.',
                ],
            },
            {
                type: 'steps',
                title: '16.2 Leave Management — Approver Flow',
                items: [
                    'Open Leave > Applications to view the pending queue.',
                    'Open the request modal and select Approve or Reject.',
                    'Add remarks and submit the decision.',
                ],
            },
            { type: 'image', src: `${IMG}/img-135.png`, caption: 'Leave management approver flow' },
            {
                type: 'steps',
                title: '16.3 Expense Management — Employee Flow',
                items: [
                    'Go to Expense > Submit, enter details and upload proof/receipts.',
                    'Track status via the Expense List.',
                ],
            },
            {
                type: 'steps',
                title: '16.3 Expense Management — Approver Flow',
                items: [
                    'Open Expense > Applications.',
                    'Review amount, type, proof, and notes.',
                    'Approve or reject with remarks and verify the updated status.',
                ],
            },
            {
                type: 'steps',
                title: '16.4 Appraisal',
                items: [
                    'Navigate to Appraisal > Submit.',
                    'Select the Project/Ship context.',
                    'Fill in appraisal fields and performance data.',
                    'Submit the appraisal report.',
                    'Review past entries in Submitted Appraisals.',
                ],
            },
            {
                type: 'steps',
                title: '16.5 User Management',
                items: [
                    'Go to User > Add to create a new user.',
                    'Assign Role, Designation, and contact details.',
                    'Save the account.',
                    'Open User List to verify, edit, or toggle access.',
                    'Deactivate users who no longer need access.',
                ],
            },
            { type: 'image', src: `${IMG}/img-137.png`, caption: 'User management' },
            {
                type: 'steps',
                title: '16.6 Role & Designation Management',
                items: [
                    'Go to Role > Add to create a new role.',
                    'Go to Designation > Add to map a job title to the role.',
                    'Update role/designation names as the organization structure evolves.',
                    'Verify all roles appear in the Role List.',
                ],
            },
            {
                type: 'steps',
                title: '16.7 Module / Permission Mapping',
                items: [
                    'Navigate to Module Assign.',
                    'Select the user Designation to configure.',
                    'Check the modules and Permission IDs to grant access.',
                    'Save the mapping.',
                    'Confirm with the user that access is visible after re-login.',
                ],
            },
            { type: 'image', src: `${IMG}/img-141.png`, caption: 'Module / permission mapping' },
            {
                type: 'note',
                variant: 'important',
                text: 'After updating permissions, always ask the user to log out and log back in to apply the changes.',
            },
            {
                type: 'steps',
                title: '16.8 Activity Log',
                items: [
                    'Navigate to Activity Log.',
                    'Set the desired date range.',
                    'Search by operation or user name.',
                    'Export or screenshot entries for audit records.',
                ],
            },
            {
                type: 'steps',
                title: '16.9 Global Settings',
                items: [
                    'Go to Global Settings > Update.',
                    'Update organization name and contact details.',
                    'Upload or replace the company logo.',
                    'Save and verify updates appear in the system header.',
                ],
            },
        ],
    },
    {
        id: 'workflows',
        number: '17',
        title: 'End-to-End Workflows',
        blocks: [
            {
                type: 'p',
                text:
                    'These guided scenarios walk you through complete real-world processes from start to finish.',
            },
            {
                type: 'steps',
                title: 'Workflow A: New Project Onboarding',
                items: [
                    'Go to Master > Projects > Create. Enter manager, dates, and metadata. Save.',
                    'Open Project List > Assign Vessels. Select unassigned hulls and map to the project.',
                    'Create baseline milestone events in the Schedule module.',
                    'Upload kickoff documents in the Document module.',
                    'Confirm the project appears in "Latest Projects" on the Dashboard.',
                ],
            },
            {
                type: 'note',
                variant: 'tip',
                text:
                    'Quality Check: ✓ Project is active and visible ✓ At least one vessel is assigned ✓ Schedule entries exist ✓ Kickoff documents are uploaded',
            },
            {
                type: 'steps',
                title: 'Workflow B: Plan Approval Lifecycle',
                items: [
                    'Create plan in Plan Approval > Create. Upload drawing files.',
                    'Assign stakeholders and post review comments in the Detail view.',
                    'Reviewers reply to comments and update individual comment statuses.',
                    'If changes are needed, upload a revision with a description note.',
                    'Finalize status, send report email, and download ZIP for project records.',
                ],
            },
            { type: 'image', src: `${IMG}/img-145.png`, caption: 'Plan approval lifecycle' },
            {
                type: 'steps',
                title: 'Workflow C: NCR Issue to Closure',
                items: [
                    'Create NCR with full non-conformance description and save.',
                    'Add initial comment requesting Root Cause Analysis (RCA).',
                    'Assign internal owner to manage the corrective action.',
                    'Update NCR status from Draft to Open.',
                    'Use threaded replies to document resolution progress and upload evidence.',
                    'Send report email to stakeholders once corrective action is verified.',
                    'Set NCR status to Close after all actions are confirmed and documented.',
                ],
            },
        ],
    },
    {
        id: 'troubleshooting',
        number: '18',
        title: 'Troubleshooting',
        blocks: [
            {
                type: 'steps',
                title: '18.1 Access Denied Errors',
                items: [
                    'Note the specific URL and module you were trying to access.',
                    'Take a screenshot of the "Access Denied" error message.',
                    'Share your User ID, Designation, and required screen URL with the system administrator.',
                    'Administrator updates module-role assignment via Module Assign.',
                    'Re-login and test access again to confirm the fix.',
                ],
            },
            {
                type: 'steps',
                title: '18.2 Missing Module',
                items: [
                    'Confirm you are logged into the correct account.',
                    'Contact the administrator to verify Module ID and permissions for your designation.',
                    'Perform a hard browser refresh (Ctrl+Shift+R) or log out and back in to clear cached permission data.',
                    'Administrator maps the missing module via Administration > Module Assign.',
                ],
            },
            {
                type: 'table',
                title: '18.3 Login Issues',
                head: ['Issue', 'Resolution'],
                rows: [
                    ['Incorrect username or password', 'Recheck credentials. Use "Forgot Password" to reset.'],
                    ['Account locked or disabled', 'Contact administrator to re-enable your account.'],
                    ['Lock screen shown on open', 'Enter your password at the Lock Screen to resume session.'],
                    ['Page not loading', 'Check internet connection. Try a different browser or clear cache.'],
                    ['Reset email not received', 'Check spam/junk folder. Ask admin to verify registered email address.'],
                ],
            },
            {
                type: 'note',
                variant: 'tip',
                text:
                    'General Tips: ✓ Always check the Notification Bell at the start of each working day. ✓ Keep your profile and email settings up to date for accurate notifications. ✓ After any admin permission changes, log out and log back in immediately. ✓ If an issue persists, note the URL, time, and steps taken — this helps the admin resolve it faster.',
            },
        ],
    },
];
